import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { RequestStatus } from '../../../stores/requests/requests-store';
import TailwindStepBasicComponent from './wizard/step1-basic-tw';
import TailwindStepSubjectMachineRelated from './wizard/step2-subject-machine-related-tw';
import TailwindStepEventsDetails from './wizard/step3-event-details-tw';
import TailwindStepDates from './wizard/step4-dates-tw';
import TailwindStepInstructors from './wizard/step5-instructors-tw';
import TailwindStepComments from './wizard/step6-comments-tw';
import { RequestWizardStore } from 'stores/requests/create-request-wizard-store';
import { IdentityService } from 'services/identity-service';
import { resolve } from 'inversify.config';
import { isNullOrEmpty, isNullOrWhiteSpaces, randomInteger } from 'utils/useful-functions';
import { NonMachineRelatedComponentViewModel } from 'site/pages/shared/events-and-requests/non-machine-related/non-machine-related';
import { EventTypeCategory, EventTypeDto } from 'stores/configuration/events-workflow/event-types-store';
import TailwindSupportDetailsContainer from './wizard-support-details-container-tw';
import { ToastComponent } from '../landing-pages/util/toast-component';
import EventService, { EventDurationRequestDto } from 'stores/events/event-service';
import TailwindMessage from '../landing-pages/mobile/alerts/message-tw';
import TailwindNonMachineRelatedComponent from 'site/pages/shared/events-and-requests/non-machine-related/non-machine-related-tw';

interface MobileNewRequestViewProps extends WithTranslation {
  onClose: (isSucess: boolean) => void;
  requestWizardStore?: RequestWizardStore;
}

interface MobileNewRequestViewState {
  steps: any[];
  errorMessages: any[];
  isBusy: boolean;
  currentStepEnum: Step;
  currentComponent: StepComponent;
  durationCalculationWarning: boolean;
}

enum Step {
  INITIAL = -1,
  BASIC = 0,
  SUBJECT = 1,
  EVENTDETAILS = 2,
  DATES = 3,
  SUPPORTDETAILS = 4,
  INSTRUCTOR = 5,
  COMMENTS = 6,
  END = 7
}

enum StepComponent {
  StepBasic,
  StepMachineRelated,
  StepNonMachineRelated,
  StepEventDetails,
  StepDate,
  StepSupportDetails,
  StepInstructor,
  StepComment
}

enum Action {
  GO_BACK = 0,
  GO_NEXT = 1
}

@connect(['requestWizardStore', RequestWizardStore])
class MobileNewRequestView extends React.Component<MobileNewRequestViewProps, MobileNewRequestViewState> {
  private get requestWizardStore() {
    return this.props.requestWizardStore;
  }

  @resolve(IdentityService)
  private identityService: IdentityService;

  @resolve(EventService)
  eventsService: EventService;

  constructor(props: MobileNewRequestViewProps) {
    super(props);
    const { t } = this.props as any;

    this.state = {
      isBusy: false,
      errorMessages: [],
      currentStepEnum: Step.BASIC,
      currentComponent: StepComponent.StepBasic,
      durationCalculationWarning: false,
      steps: [
        {
          title: t('Basic'),
          visible: true,
          internalStep: Step.BASIC,
          onClick: e => {
            e.preventDefault();
          }
        },
        {
          title: t('Subject'),
          visible: false,
          internalStep: Step.SUBJECT,
          onClick: e => {
            e.preventDefault();
          }
        },
        {
          title: t('Event Details'),
          visible: false,
          internalStep: Step.EVENTDETAILS,
          onClick: e => {
            e.preventDefault();
          }
        },
        {
          title: t('Dates'),
          visible: true,
          internalStep: Step.DATES,
          onClick: e => {
            e.preventDefault();
          }
        },
        {
          title: t('Support Details'),
          visible: false,
          internalStep: Step.SUPPORTDETAILS,
          onClick: e => {
            e.preventDefault();
          }
        },

        {
          title: t('Instructor'),
          visible: false,
          internalStep: Step.INSTRUCTOR,
          onClick: e => {
            e.preventDefault();
          }
        },
        {
          title: t('Comments'),
          visible: true,
          internalStep: Step.COMMENTS,
          onClick: e => {
            e.preventDefault();
          }
        }
      ]
    };

    this.requestWizardStore.createNew({
      requestId: Math.random().toString(),
      status: RequestStatus.Draft,
      customization: null,
      deliveryMethod: null,
      isMachineRelated: null,
      withPattern: false,
      hasModifiedPattern: false,
      language: null,
      eventType: null,
      eventLocation: null,
      requestLocation: null,
      priority: null,
      role: null,
      isTechnical: null,
      eventsColor: null,
      hasSupportDetails: false,
      isRequestDetails: false,
      isParticipantYes: false,
      studentsMandatory: true,
      isEventDetails: false,
      isInstructorYes: false,
      requestReason: null,
      requestReasonComments: null,
      requestReasonEventTypes: [],
      category: null,
      manualTitle: false,
      manualStudents: false,
      machineModels: [],
      nmrCluster: null,
      nmrFunctionalArea: null,
      nmrFunctionalSubArea: null,
      nmrTrainingName: null,
      pattern: null,
      title: '',
      trainingLevel: null,
      studentsNumber: null,
      students: [],
      endDate: null,
      startDate: null,
      desiredEventDuration: null,
      instructor: null,
      instructorLocation: null,
      comments: '',
      requestOwnerId: this.identityService.userId,
      requestingLocationId: null,
      plcNames: [],
      machineRelated: false,
      machines: [],
      supportDetails: {
        requestPreApprovedDuringASP: false,
        totalCostLocationA: 0,
        totalCostLocationB: 0,
        totalCostLocationC: 0,
        totalCostLocationD: 0,
        supportPositions: [],
        newSupportPositions: [],
        isNewSupportPositionModel: true,
        totalRequestedManDays: 0,
        totalRequestedManMonths: 0,
        totalRequestedWorkingManHours: 0,
        totalTheoreticalCost: 0
      },
      calculatedEventDuration: 0,
      plannedDuration: '0',
      userEventDuration: 0,
      requestPreApprovedDuringASPCheck: false
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: MobileNewRequestViewProps) {
    if (this.requestWizardStore.state.result && this.requestWizardStore.state.result.isSuccess) {
      nextProps.onClose(this.requestWizardStore.state.result.isSuccess);
    }
  }

  @autobind
  private onCreateNewItem() {
    const currentUser = this.identityService.getUserInfo();

    this.requestWizardStore.change({
      requestId: Math.random().toString(),
      requestOwnerId: currentUser.userId
    });

    if (!this.requestWizardStore.state.item.title) {
      this.requestWizardStore.change({
        title: `Request_${randomInteger(100, 1000000)}`
      });
    }

    this.requestWizardStore
      .submitWizzard()
      .then(res => this.showToast(res))
      .catch(err => {
        console.error(err);
        ToastComponent({ text: this.props.t('Request save failed'), type: 'warning-toast' });
      });
  }

  private showToast = res => {
    const isDraft = this.props.requestWizardStore.state.item.status === RequestStatus.Draft;

    if (res.isSuccess && isDraft) {
      ToastComponent({ text: this.props.t('Save Request Draft successfully!'), type: 'success-toast' });
    } else if (res.isSuccess) {
      ToastComponent({ text: this.props.t('Send Request successfully!'), type: 'success-toast' });
    } else {
      this.props.requestWizardStore.state.result.messages.forEach(msg => ToastComponent({ text: msg.body, type: 'warning-toast' }));
    }
  };

  @autobind
  private onCreateNewItemDraft() {
    const { item } = this.requestWizardStore.state;
    this.requestWizardStore.change({
      status: RequestStatus.Draft,
      machineModels: item.machines.map(({ cluster, equipmentType, machineModel, machineUnits, oem, plcTypes }) => ({
        machineRelatedClusterId: cluster && cluster.id,
        machineRelatedClusterName: cluster && cluster.title,
        equipmentTypeId: equipmentType && equipmentType.id,
        equipmentTypeName: equipmentType && equipmentType.title,
        oemId: oem && oem.id,
        oemName: oem && oem.title,
        machineModelId: machineModel && machineModel.id,
        machineModelName: machineModel && machineModel.title,
        plcTypeRequestMachines: plcTypes,
        machineUnitRequestMachines: machineUnits
      }))
    });

    this.onCreateNewItem();
  }

  @autobind
  private nextOrPending() {
    const isLastStep = this.state.currentStepEnum === Step.COMMENTS;
    if (isLastStep) {
      this.onCreateNewItemPending();
    } else {
      this.handleNext(Action.GO_NEXT);
    }
  }

  @autobind
  private onCreateNewItemPending() {
    const { item } = this.requestWizardStore.state;
    this.requestWizardStore.change({
      status: RequestStatus.Pending,
      machineModels: item.machines.map(({ cluster, equipmentType, machineModel, machineUnits, oem, plcTypes }) => ({
        machineRelatedClusterId: cluster && cluster.id,
        machineRelatedClusterName: cluster && cluster.title,
        equipmentTypeId: equipmentType && equipmentType.id,
        equipmentTypeName: equipmentType && equipmentType.title,
        oemId: oem && oem.id,
        oemName: oem && oem.title,
        machineModelId: machineModel && machineModel.id,
        machineModelName: machineModel && machineModel.title,
        plcTypeRequestMachines: plcTypes,
        machineUnitRequestMachines: machineUnits
      }))
    });

    this.onCreateNewItem();
  }

  @autobind
  private onCancelNewItem() {
    this.requestWizardStore.clear();
    this.props.onClose(false);
  }

  @autobind
  private handleNext(action) {
    if (action === Action.GO_NEXT) {
      const validationResult = this.validateCurrentStep();
      const isStepInvalid = validationResult && validationResult.isInvalid();

      if (isStepInvalid) {
        this.requestWizardStore.Validate(validationResult);
      } else {
        this.requestWizardStore.state.result = null;
        const [nextStep, component] = this.getNext(this.state.currentStepEnum, this.props.requestWizardStore.state.item);
        if ((nextStep as any) === StepComponent.StepEventDetails)
          this.recalculateEventDuration().catch(_ => {
            this.setState({ isBusy: false });
          });
        this.setState({ currentStepEnum: nextStep, currentComponent: component });
      }
    }
    //action GO BACK
    else if (this.state.currentStepEnum !== Step.BASIC) {
      const [backStep, component] = this.getBack(this.state.currentStepEnum, this.props.requestWizardStore.state.item);

      this.setState({ currentStepEnum: backStep, currentComponent: component });
    }
  }

  private validateCurrentStep = () => {
    const { currentStepEnum, currentComponent } = this.state;
    const { requestWizardStore } = this.props;
    const { item } = requestWizardStore.state;

    let validationResult = null;

    if (currentStepEnum === Step.BASIC) {
      validationResult = requestWizardStore.validateBasicStep(item);
    } else if (currentStepEnum === Step.SUBJECT && currentComponent === StepComponent.StepMachineRelated && item.isRequestDetails) {
      validationResult = requestWizardStore.validateSubjectStep(item);
    } else if (currentStepEnum === Step.SUBJECT && currentComponent === StepComponent.StepNonMachineRelated && item.isRequestDetails) {
      validationResult = requestWizardStore.validateNMRSubjectStep(item);
    } else if (currentStepEnum === Step.EVENTDETAILS) {
      validationResult = requestWizardStore.validateEventDetailsStep(item);
    } else if (currentStepEnum === Step.DATES) {
      validationResult = requestWizardStore.validateDatesStep(item);
    } else if (currentStepEnum === Step.SUPPORTDETAILS) {
      validationResult = requestWizardStore.validateSupportDetailsStep(item);
    } else if (currentStepEnum === Step.INSTRUCTOR) {
      validationResult = requestWizardStore.validateInstructorStep(item);
    } else if (currentStepEnum === Step.COMMENTS) {
      validationResult = requestWizardStore.validateCommentsStep(item);
    }
    return validationResult;
  };

  getBack = (prev: Step, event: any): [Step, StepComponent] => {
    let st = prev;

    if (st === Step.COMMENTS) {
      if (
        event.isInstructorYes &&
        EventTypeCategory[((event.eventType as any) as EventTypeDto).eventTypeCategory] !== EventTypeCategory.Extended
      )
        return [Step.INSTRUCTOR, StepComponent.StepInstructor];
      st = Step.INSTRUCTOR;
    }

    if (st === Step.INSTRUCTOR) {
      st = Step.SUPPORTDETAILS;
    }

    if (st === Step.SUPPORTDETAILS) {
      return [Step.DATES, StepComponent.StepDate];
    }

    if (st === Step.DATES) {
      if (event.isEventDetails) return [Step.EVENTDETAILS, StepComponent.StepEventDetails];
      st = Step.EVENTDETAILS;
    }

    if (st === Step.EVENTDETAILS) {
      if (
        event &&
        event.eventType &&
        (EventTypeCategory[(event.eventType as EventTypeDto).eventTypeCategory] === EventTypeCategory.Vacations ||
          event.eventType.eventTypeCategory === 'Extended')
      ) {
        return [Step.BASIC, StepComponent.StepBasic];
      }
      if (event.isMachineRelated) {
        return [Step.SUBJECT, StepComponent.StepMachineRelated];
      } else {
        return [Step.SUBJECT, StepComponent.StepNonMachineRelated];
      }
    }

    if (st === Step.SUBJECT) {
      return [Step.BASIC, StepComponent.StepBasic];
    }

    if (st === Step.INITIAL) return [Step.INITIAL, StepComponent.StepBasic];

    if (st === Step.BASIC) {
      return [Step.BASIC, StepComponent.StepBasic];
    }
  };

  getNext = (prev: Step, event: any): [Step, StepComponent] => {
    if (prev === Step.INITIAL) return [Step.BASIC, StepComponent.StepBasic];

    if (prev === Step.BASIC) {
      if (
        event &&
        event.eventType &&
        (EventTypeCategory[(event.eventType as EventTypeDto).eventTypeCategory] === EventTypeCategory.Vacations ||
          EventTypeCategory[((event.eventType as any) as EventTypeDto).eventTypeCategory] === EventTypeCategory.Extended)
      ) {
        return [Step.DATES, StepComponent.StepDate];
      }

      if (event.isMachineRelated) return [Step.SUBJECT, StepComponent.StepMachineRelated];
      return [Step.SUBJECT, StepComponent.StepNonMachineRelated];
    }

    if (prev === Step.SUBJECT) {
      if (event.isEventDetails) return [Step.EVENTDETAILS, StepComponent.StepEventDetails];
      return [Step.DATES, StepComponent.StepDate];
    }

    if (prev === Step.EVENTDETAILS) return [Step.DATES, StepComponent.StepDate];

    if (prev === Step.DATES) {
      if (event.hasSupportDetails) return [Step.SUPPORTDETAILS, StepComponent.StepSupportDetails];
      if (event.isInstructorYes) return [Step.INSTRUCTOR, StepComponent.StepInstructor];
      return [Step.COMMENTS, StepComponent.StepComment];
    }

    if (prev === Step.SUPPORTDETAILS) {
      if (
        event.isInstructorYes &&
        EventTypeCategory[((event.eventType as any) as EventTypeDto).eventTypeCategory] !== EventTypeCategory.Extended
      )
        return [Step.INSTRUCTOR, StepComponent.StepInstructor];
      return [Step.COMMENTS, StepComponent.StepComment];
    }

    if (prev === Step.INSTRUCTOR) return [Step.COMMENTS, StepComponent.StepComment];

    if (prev === Step.COMMENTS) return [Step.END, StepComponent.StepComment];

    return [Step.END, StepComponent.StepComment];
  };

  createNonMachineRelatedComponent = (): React.ReactNode => {
    const { t, requestWizardStore } = this.props;
    const { nmrCluster, nmrFunctionalArea, nmrFunctionalSubArea, nmrTrainingName, category } = requestWizardStore.state.item;

    const nMR: NonMachineRelatedComponentViewModel = {
      cluster: nmrCluster,
      functionalArea: nmrFunctionalArea,
      functionalSubArea: nmrFunctionalSubArea,
      trainingName: nmrTrainingName
    };
    return (
      <>
        <h3 className="machine-related__title">{t('NON-MACHINE RELATED')}</h3>
        <TailwindNonMachineRelatedComponent
          category={category}
          className="wizard__no-machine-related"
          value={nMR}
          readOnly={false}
          onChange={this.handleNMRChange}
        />
      </>
    );
  };

  loading: boolean = false;

  @autobind
  private getStepContent() {
    const { currentComponent: nextComponent, durationCalculationWarning } = this.state;

    if (nextComponent === StepComponent.StepBasic)
      return (
        <TailwindStepBasicComponent
          onSelectEventTypeBegin={() => this.setState({ isBusy: true })}
          onSelectEventTypeEnd={() => this.setState({ isBusy: false })}
          {...this.props}
        />
      );
    else if (nextComponent === StepComponent.StepMachineRelated) return <TailwindStepSubjectMachineRelated {...this.props} />;
    else if (nextComponent === StepComponent.StepNonMachineRelated) return this.createNonMachineRelatedComponent();
    else if (nextComponent === StepComponent.StepDate)
      return <TailwindStepDates {...this.props} durationWarning={durationCalculationWarning} />;
    else if (nextComponent === StepComponent.StepEventDetails) return <TailwindStepEventsDetails {...this.props} />;
    else if (nextComponent === StepComponent.StepSupportDetails)
      return <TailwindSupportDetailsContainer {...this.props} readOnly={false} />;
    else if (nextComponent === StepComponent.StepInstructor) return <TailwindStepInstructors {...this.props} />;
    else if (nextComponent === StepComponent.StepComment) return <TailwindStepComments {...this.props} />;

    return nextComponent;
  }

  getDurationRequestDto = (): EventDurationRequestDto => {
    const { isMachineRelated, machineModels, nmrCluster, nmrFunctionalArea, ...rest } = this.props.requestWizardStore.state.item;
    const { nmrTrainingName, eventType, role, trainingLevel } = rest;
    const rows = (machineModels || []).map(({ machineModelId, oemId, ...x }) => ({
      clusterId: x?.machineRelatedClusterId,
      equipmentTypeId: x?.equipmentTypeId,
      machineModelId,
      machineUnitsId: x.machineUnitRequestMachines,
      oemId
    }));
    return {
      isMachineRelated,
      mrRequest: isMachineRelated
        ? { originalEventTypeId: eventType?.originalEventTypeId, professionId: role?.id, trainingLevelId: trainingLevel?.id, rows }
        : null,
      nmrRequest: !isMachineRelated
        ? { nmrClusterId: nmrCluster?.id, nmrFunctionalAreaId: nmrFunctionalArea?.id, nmrTrainingNameId: nmrTrainingName?.id }
        : null
    };
  };

  private isValidRequest = (dto: EventDurationRequestDto) => {
    if (dto == null) return false;
    if (dto.isMachineRelated) {
      if (
        dto.mrRequest == null ||
        isNullOrWhiteSpaces(dto.mrRequest.originalEventTypeId) ||
        isNullOrWhiteSpaces(dto.mrRequest.professionId) ||
        isNullOrWhiteSpaces(dto.mrRequest.trainingLevelId) ||
        (dto.mrRequest.rows || []).length === 0
      )
        return false;
      return dto.mrRequest.rows.all(r => !isNullOrWhiteSpaces(r.clusterId) && !isNullOrWhiteSpaces(r.equipmentTypeId));
    } else return !isNullOrWhiteSpaces(dto.nmrRequest?.nmrClusterId) && !isNullOrWhiteSpaces(dto.nmrRequest?.nmrFunctionalAreaId);
  };

  private recalculateEventDuration = () => {
    if (this.loading) return;
    const { requestWizardStore } = this.props;
    const dto = this.getDurationRequestDto();
    this.setState({ isBusy: true });
    const isValid = this.isValidRequest(dto);
    if (!isValid) return Promise.reject();
    return new Promise<void>(resolveCall => {
      this.eventsService
        .calculateEventDuration(dto)
        .then(({ days }) => {
          const w = { ...requestWizardStore.state.item };
          w.calculatedEventDuration = days;
          if (isNullOrEmpty(w.plannedDuration) || w.plannedDuration === '0 w/d' || w.plannedDuration === '0') {
            w.plannedDuration = `${days}`;
            w.userEventDuration = days;
          }
          requestWizardStore.change({ calculatedEventDuration: days, plannedDuration: `${days}`, userEventDuration: days });
          this.setState({ isBusy: false, durationCalculationWarning: false });
          resolveCall();
        })
        .catch(_ => {
          const w = { ...requestWizardStore.state.item };
          w.calculatedEventDuration = 0;
          if (isNullOrEmpty(w.plannedDuration)) {
            w.plannedDuration = '0';
            w.userEventDuration = 0;
          }
          this.setState({ isBusy: false, durationCalculationWarning: true });
          resolveCall();
        });
    });
  };

  private handleNMRChange = (value: NonMachineRelatedComponentViewModel) => {
    this.props.requestWizardStore.change({
      nmrCluster: value.cluster,
      nmrFunctionalArea: value.functionalArea,
      nmrFunctionalSubArea: value.functionalSubArea,
      nmrTrainingName: value.trainingName
    });
  };

  public render() {
    const { t, requestWizardStore } = this.props;
    const { result } = requestWizardStore.state;
    const { currentStepEnum } = this.state;

    return (
      <div className="p-4 relative" style={{ paddingBottom: '120px' /* espacio para toolbar */ }}>
        {/* <div className="bg-white rounded-lg border border-gray-300 shadow-2xl w-full max-w-sm p-4 relative"> */}
        <button onClick={this.onCancelNewItem} className="flex items-center gap-1 text-sm text-black-600 mb-2">
          ← {t('Close')}
        </button>

        <div className="flex items-center mb-4">
          <span className="text-lg font-semibold flex-1">
            <h3>{t('Request Creation.')}</h3>
          </span>
          <span className="flex flex-1 justify-center items-center">
            <span className="font-semibold mr-2">{this.state.steps[currentStepEnum].title}</span>
          </span>
        </div>

        {(this.state.isBusy || this.props.requestWizardStore?.state.isBusy) && (
          <div className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
            <span className="sr-only">{t('Loading')}</span>
            <svg className="animate-spin h-10 w-10 text-primary-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          </div>
        )}

        {this.requestWizardStore.state.result && !this.requestWizardStore.state.result.isSuccess && (
          <TailwindMessage
            icon="exclamation circle"
            error
            header={t('An error ocurred')}
            list={this.requestWizardStore.state.result.messages.map(o => o.body)}
            className="mb-4"
          />
        )}

        {result && !result.isSuccess && (
          <section role="alert" className="bg-red-50 border border-red-300 p-4 rounded mb-6 text-red-700">
            <strong>{t('An error occurred')}</strong>
            <ul className="list-disc list-inside mt-2">
              {result.messages.map((m, i) => (
                <li key={`resmsg-${i}`}>{m.body}</li>
              ))}
            </ul>
          </section>
        )}

        {this.requestWizardStore.state.item && this.getStepContent()}

        {/* Botones */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 58,
            right: 0,
            background: 'white',
            borderTop: '1px solid #ddd',
            padding: '0.5rem 1rem',
            zIndex: 1000
          }}
          className="flex gap-2"
        >
          {currentStepEnum > 0 && (
            <button
              className="flex-1 bg-primary-600 text-white py-2 rounded disabled:opacity-50"
              onClick={side => this.handleNext(Action.GO_BACK)}
            >
              {t('Back')}
            </button>
          )}

          <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={this.onCancelNewItem}>
            {t('Cancel')}
          </button>

          {this.state.currentStepEnum === Step.COMMENTS && (
            <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={this.onCreateNewItemDraft}>
              {t('Save Draft')}
            </button>
          )}

          <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={this.nextOrPending}>
            {this.state.currentStepEnum === Step.COMMENTS ? t('Send') : t('Next')}
          </button>
        </div>
      </div>

      // </div>
    );
  }
}

// Wire up the React component to the Redux store
export default withTranslation()(MobileNewRequestView);
